import { FormulaTokenTipoEnum } from '@shared/enums/formula/formula-token-tipo.enum';
import { FormulaExpressaoToken } from './formula-expressao-token';

/** Delimita uma função na lista plana de tokens mantida pelo editor. */
export class FormulaFuncaoBloco {
    id: string;
    start: number;
    end: number;
    nome: string;
    nivel: number;
    skeleton: Set<number>;
}

/**
 * Reconstrói os blocos de função sem alterar o formato plano persistido da expressão.
 * O nível começa em zero e permite aplicar o ciclo visual de três cores nas funções aninhadas.
 */
export function mapearBlocosFuncao(tokens: FormulaExpressaoToken[]): FormulaFuncaoBloco[] {
    const blocos: FormulaFuncaoBloco[] = [];
    const parenteses: Array<{ funcao: FormulaFuncaoBloco | null }> = [];

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.tipo === FormulaTokenTipoEnum.Parentese && token.valor === '(') {
            const funcao = tokens[i - 1];
            if (funcao?.tipo === FormulaTokenTipoEnum.Funcao) {
                const bloco: FormulaFuncaoBloco = {
                    id: `funcao-${i - 1}`,
                    start: i - 1,
                    end: tokens.length - 1,
                    nome: funcao.valor.toLowerCase(),
                    nivel: parenteses.filter(item => item.funcao !== null).length,
                    skeleton: new Set<number>([i - 1, i]),
                };
                parenteses.push({ funcao: bloco });
            } else {
                parenteses.push({ funcao: null });
            }
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Separador) {
            parenteses.at(-1)?.funcao?.skeleton.add(i);
            continue;
        }

        if (token.tipo === FormulaTokenTipoEnum.Parentese && token.valor === ')' && parenteses.length > 0) {
            const fechamento = parenteses.pop()!;
            if (fechamento.funcao) {
                fechamento.funcao.end = i;
                fechamento.funcao.skeleton.add(i);
                blocos.push(fechamento.funcao);
            }
        }
    }

    return blocos.sort((a, b) => a.start - b.start || b.end - a.end);
}

/**
 * Marca uma expressão recém-parseada. A leitura posicional acontece uma única vez;
 * depois disso o editor preserva os identificadores durante todas as interações.
 */
export function marcarBlocosFuncao(
    tokens: FormulaExpressaoToken[],
    criarId: () => string,
): FormulaFuncaoBloco[] {
    tokens.forEach(token => {
        token.funcaoGrupoIds = [];
        token.funcaoEstruturalId = undefined;
    });

    const blocos = mapearBlocosFuncao(tokens);
    for (const bloco of blocos) {
        const id = criarId();
        for (let i = bloco.start; i <= bloco.end; i++) {
            tokens[i].funcaoGrupoIds = [...(tokens[i].funcaoGrupoIds ?? []), id];
        }
        bloco.skeleton.forEach(index => tokens[index].funcaoEstruturalId = id);
    }

    return mapearBlocosFuncaoMarcados(tokens);
}

/** Reconstrói os intervalos usando somente as marcas estáveis mantidas nos tokens. */
export function mapearBlocosFuncaoMarcados(tokens: FormulaExpressaoToken[]): FormulaFuncaoBloco[] {
    const ids = Array.from(new Set(tokens.flatMap(token => token.funcaoGrupoIds ?? [])));
    const blocos: FormulaFuncaoBloco[] = [];

    for (const id of ids) {
        const indices = tokens
            .map((token, index) => token.funcaoGrupoIds?.includes(id) ? index : -1)
            .filter(index => index >= 0);
        if (indices.length === 0) continue;

        const skeleton = new Set(tokens
            .map((token, index) => token.funcaoEstruturalId === id ? index : -1)
            .filter(index => index >= 0));
        const inicio = [...skeleton].find(index => tokens[index].tipo === FormulaTokenTipoEnum.Funcao);
        if (inicio === undefined) continue;

        const grupos = tokens[inicio].funcaoGrupoIds ?? [];
        blocos.push({
            id,
            start: Math.min(...indices),
            end: Math.max(...indices),
            nome: tokens[inicio].valor.toLowerCase(),
            nivel: Math.max(0, grupos.indexOf(id)),
            skeleton,
        });
    }

    return blocos.sort((a, b) => a.start - b.start || b.end - a.end);
}

/** Retorna a função mais interna que contém o token informado. */
export function obterBlocoFuncaoDoToken(blocos: FormulaFuncaoBloco[], tokenIndex: number): FormulaFuncaoBloco | null {
    return blocos
        .filter(bloco => bloco.start <= tokenIndex && bloco.end >= tokenIndex)
        .sort((a, b) => (a.end - a.start) - (b.end - b.start))[0] ?? null;
}

/** Retorna a função dona de um token estrutural: nome, parêntese ou separador. */
export function obterBlocoFuncaoEstrutural(blocos: FormulaFuncaoBloco[], tokenIndex: number): FormulaFuncaoBloco | null {
    return blocos.find(bloco => bloco.skeleton.has(tokenIndex)) ?? null;
}
